import { Check, Info, Loader2, Pencil } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useSession } from "@/auth/client";
import { PageHeader } from "@/components/shared/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { currentFiscalYear, formatFY } from "@/lib/fiscal";
import {
  formatOpsValue,
  type OpsMetricConfig,
  RATING_LABELS,
  ratingFor,
} from "@/lib/ops-scorecard";
import { cn } from "@/lib/utils";
import { type OpsDrillDownRequest, OpsDrillDownSheet } from "./ops-drill-down-sheet";

// ── Types (mirror /api/ops-dashboards responses) ─────────────────────

interface MetricCell {
  numerator: number;
  denominator: number;
  value: number | null;
}

interface QuarterRow {
  label: string;
  start: string;
  end: string;
  status: "complete" | "current" | "future";
  metrics: Record<string, MetricCell | null>;
}

interface OpsDashboardResponse {
  fiscalYear: number;
  asOf: string;
  jewelerCount?: number;
  metrics: OpsMetricConfig[];
  quarters: QuarterRow[];
  fyTotals: Record<string, MetricCell | null>;
  error?: string;
}

interface OpsScorecardPageProps {
  dashboard: "manufacturing" | "operations";
  title: string;
  description: string;
  /** Render without the outer page padding/PageHeader — for embedding inside another page. */
  embedded?: boolean;
}

// ── Rating chip ──────────────────────────────────────────────────────

const RATING_STYLES: Record<number, string> = {
  1: "bg-red-500/10 text-red-700 border-red-500/30",
  2: "bg-amber-500/10 text-amber-700 border-amber-500/30",
  3: "bg-brand-gold/15 text-brand-gold border-brand-gold/40",
  4: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
  5: "bg-emerald-600/15 text-emerald-800 border-emerald-600/40",
};

function RatingChip({ config, value }: { config: OpsMetricConfig; value: number | null }) {
  const rating = ratingFor(config, value);
  if (rating === null) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          className={cn(
            "inline-flex h-5 min-w-5 items-center justify-center rounded-full border px-1 text-[10px] font-semibold tabular-nums",
            RATING_STYLES[rating],
          )}
        >
          {rating}
        </span>
      </TooltipTrigger>
      <TooltipContent>{RATING_LABELS[rating - 1]}</TooltipContent>
    </Tooltip>
  );
}

// ── Metric info tooltip (what the data is + where it comes from) ─────

function MetricInfo({ config }: { config: OpsMetricConfig }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex cursor-help text-muted-foreground/70 hover:text-muted-foreground">
          <Info className="h-3 w-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs space-y-1.5">
        <p className="font-medium">{config.label}</p>
        <p>
          <span className="font-medium">How it's calculated:</span> {config.formula}
        </p>
        <p>
          <span className="font-medium">Data source:</span> {config.source}
        </p>
        <p className="text-[10px] opacity-80">Click any value to see the underlying records.</p>
      </TooltipContent>
    </Tooltip>
  );
}

// ── Page ─────────────────────────────────────────────────────────────

export function OpsScorecardPage({
  dashboard,
  title,
  description,
  embedded = false,
}: OpsScorecardPageProps) {
  const [data, setData] = useState<OpsDashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFY, setSelectedFY] = useState(currentFiscalYear());
  const [drillDown, setDrillDown] = useState<OpsDrillDownRequest | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/ops-dashboards/${dashboard}?fy=${selectedFY}`);
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }
      const json = (await res.json()) as OpsDashboardResponse;
      if (json.error) throw new Error(json.error);
      setData(json);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  }, [dashboard, selectedFY]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const availableFYs = [currentFiscalYear(), currentFiscalYear() - 1];

  const fySwitcher = (
    <div className="flex flex-wrap items-center gap-2">
      {availableFYs.map((fy) => (
        <Button
          key={fy}
          type="button"
          size="sm"
          variant={selectedFY === fy ? "default" : "outline"}
          className={
            selectedFY === fy ? "bg-brand-gold text-brand-navy hover:bg-brand-gold/90" : undefined
          }
          onClick={() => setSelectedFY(fy)}
        >
          {formatFY(fy)}
        </Button>
      ))}
    </div>
  );

  const shell = (children: ReactNode) => (
    <TooltipProvider>
      <div className={embedded ? "space-y-6" : "space-y-6 p-6"}>
        {embedded ? (
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-serif text-lg font-semibold text-foreground">{title}</h2>
              <p className="text-sm text-muted-foreground">{description}</p>
            </div>
            {fySwitcher}
          </div>
        ) : (
          <PageHeader title={title} description={description} actions={fySwitcher} />
        )}
        {children}
      </div>
    </TooltipProvider>
  );

  if (loading) {
    return shell(
      <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </>,
    );
  }

  if (error || !data) {
    return shell(
      <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error ?? "No data available."}
      </div>,
    );
  }

  const activeQuarter =
    data.quarters.find((q) => q.status === "current") ??
    [...data.quarters].reverse().find((q) => q.status === "complete") ??
    data.quarters[0];

  if (!activeQuarter) {
    return shell(
      <div className="rounded-md border border-muted bg-muted/30 p-6 text-sm text-muted-foreground">
        No quarters available for this fiscal year.
      </div>,
    );
  }

  return shell(
    <>
      <CurrentQuarterCards data={data} quarter={activeQuarter} onDrill={setDrillDown} />
      <ScorecardTable data={data} onDrill={setDrillDown} />
      {dashboard === "manufacturing" && (
        <JewelerCountCard jewelerCount={data.jewelerCount ?? 0} onSaved={fetchData} />
      )}
      <RatingBandsCard metrics={data.metrics} />
      <OpsDrillDownSheet
        dashboard={dashboard}
        fiscalYear={selectedFY}
        drillDown={drillDown}
        onClose={() => setDrillDown(null)}
      />
    </>,
  );
}

// ── Current quarter KPI cards ────────────────────────────────────────

function CurrentQuarterCards({
  data,
  quarter,
  onDrill,
}: {
  data: OpsDashboardResponse;
  quarter: QuarterRow;
  onDrill: (req: OpsDrillDownRequest) => void;
}) {
  return (
    <div>
      <div className="mb-2 text-[10px] tracking-widest uppercase text-muted-foreground">
        {quarter.label} · {quarter.status === "current" ? "Quarter to date" : "Final"} · as of{" "}
        {data.asOf}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {data.metrics.map((config) => {
          const cell = quarter.metrics[config.key] ?? null;
          const value = cell?.value ?? null;
          return (
            <Card key={config.key} className="relative overflow-hidden">
              <CardContent className="pt-6">
                <div className="flex items-center gap-1.5 text-[10px] tracking-widest uppercase text-muted-foreground">
                  {config.label}
                  <MetricInfo config={config} />
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  {config.pending || cell === null ? (
                    <span className="font-serif text-3xl text-foreground tabular-nums">
                      {config.pending ? "—" : formatOpsValue(config.format, value)}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() =>
                        onDrill({
                          metric: config.key,
                          quarter: quarter.label,
                          label: `${config.label} — ${quarter.label} ${formatFY(data.fiscalYear)}`,
                        })
                      }
                      className="cursor-pointer font-serif text-3xl text-foreground tabular-nums hover:underline underline-offset-4 decoration-brand-gold/60"
                    >
                      {formatOpsValue(config.format, value)}
                    </button>
                  )}
                  {!config.pending && <RatingChip config={config} value={value} />}
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {config.pending ? (
                    <Badge
                      variant="outline"
                      className="border-brand-gold/40 text-brand-gold text-[10px]"
                    >
                      Pending ticket data
                    </Badge>
                  ) : (
                    <>Benchmark {formatOpsValue(config.format, config.benchmark)}</>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ── Quarterly scorecard table ────────────────────────────────────────

function ScorecardTable({
  data,
  onDrill,
}: {
  data: OpsDashboardResponse;
  onDrill: (req: OpsDrillDownRequest) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-lg">{formatFY(data.fiscalYear)} Scorecard</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-56">Metric</TableHead>
              <TableHead className="w-36" />
              <TableHead className="text-right">FY</TableHead>
              {data.quarters.map((q) => (
                <TableHead key={q.label} className="text-right">
                  {q.label}
                  {q.status === "current" && (
                    <span className="ml-1 text-[9px] text-brand-gold align-top">QTD</span>
                  )}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.metrics.map((config) => (
              <MetricRows key={config.key} config={config} data={data} onDrill={onDrill} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function MetricRows({
  config,
  data,
  onDrill,
}: {
  config: OpsMetricConfig;
  data: OpsDashboardResponse;
  onDrill: (req: OpsDrillDownRequest) => void;
}) {
  const fyCell = data.fyTotals[config.key] ?? null;
  const fyLabel = formatFY(data.fiscalYear);

  return (
    <>
      <TableRow className="border-b-0 hover:bg-transparent">
        <TableCell rowSpan={2} className="align-top">
          <div className="flex items-center gap-1.5 font-serif text-sm text-foreground">
            {config.label}
            <MetricInfo config={config} />
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground max-w-48">{config.description}</div>
          {config.pending && (
            <Badge
              variant="outline"
              className="mt-1.5 border-brand-gold/40 text-brand-gold text-[10px]"
            >
              Pending ticket data
            </Badge>
          )}
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">Expected Benchmark</TableCell>
        <TableCell className="text-right text-xs text-muted-foreground tabular-nums bg-muted/30">
          {formatOpsValue(config.format, config.benchmark)}
        </TableCell>
        {data.quarters.map((q) => (
          <TableCell
            key={q.label}
            className="text-right text-xs text-muted-foreground tabular-nums bg-muted/30"
          >
            {formatOpsValue(config.format, config.benchmark)}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        <TableCell className="text-xs font-medium">Actual</TableCell>
        <ActualCell
          config={config}
          cell={fyCell}
          emphasize
          onDrill={() =>
            onDrill({ metric: config.key, label: `${config.label} — ${fyLabel} (full year)` })
          }
        />
        {data.quarters.map((q) => (
          <ActualCell
            key={q.label}
            config={config}
            cell={q.status === "future" ? null : (q.metrics[config.key] ?? null)}
            onDrill={
              q.status === "future"
                ? undefined
                : () =>
                    onDrill({
                      metric: config.key,
                      quarter: q.label,
                      label: `${config.label} — ${q.label} ${fyLabel}`,
                    })
            }
          />
        ))}
      </TableRow>
    </>
  );
}

function ActualCell({
  config,
  cell,
  emphasize = false,
  onDrill,
}: {
  config: OpsMetricConfig;
  cell: MetricCell | null;
  emphasize?: boolean;
  onDrill?: () => void;
}) {
  const value = config.pending ? null : (cell?.value ?? null);
  const drillable = !config.pending && cell !== null && onDrill !== undefined;
  return (
    <TableCell className="text-right">
      <div className="flex items-center justify-end gap-1.5">
        {drillable ? (
          <button
            type="button"
            onClick={onDrill}
            className={cn(
              "cursor-pointer tabular-nums text-sm hover:underline underline-offset-2 decoration-brand-gold/60",
              emphasize && "font-semibold",
            )}
          >
            {formatOpsValue(config.format, value)}
          </button>
        ) : (
          <span className={cn("tabular-nums text-sm", emphasize && "font-semibold")}>
            {formatOpsValue(config.format, value)}
          </span>
        )}
        <RatingChip config={config} value={value} />
      </div>
      {cell && value !== null && config.format === "percent" && (
        <div className="text-[10px] text-muted-foreground tabular-nums">
          {cell.numerator.toLocaleString()} / {cell.denominator.toLocaleString()}
        </div>
      )}
    </TableCell>
  );
}

// ── Jeweler count (capacity denominator) ─────────────────────────────

function JewelerCountCard({
  jewelerCount,
  onSaved,
}: {
  jewelerCount: number;
  onSaved: () => void;
}) {
  const { data: session } = useSession();
  const isAdmin = (session?.user as { role?: string } | undefined)?.role === "admin";
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(jewelerCount));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch("/api/ops-dashboards/jeweler-count", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jewelerCount: Number(value) }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? "Failed to save");
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[10px] tracking-widest uppercase text-muted-foreground">
              Capacity Inputs
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Capacity = rings completed ÷ (Mon–Fri working days ×{" "}
              <span className="font-medium text-foreground">{jewelerCount}</span> jeweler
              {jewelerCount === 1 ? "" : "s"}). Per-jeweler breakdown activates once a Setter
              property exists in HubSpot.
            </div>
            {error && <p className="mt-1 text-xs text-destructive">{error}</p>}
          </div>
          {isAdmin &&
            (editing ? (
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  max={100}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="h-8 w-20"
                />
                <Button size="sm" onClick={save} disabled={saving}>
                  {saving ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Check className="h-3.5 w-3.5" />
                  )}
                  Save
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setValue(String(jewelerCount));
                  setEditing(true);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit jewelers
              </Button>
            ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ── Rating bands reference ───────────────────────────────────────────

function RatingBandsCard({ metrics }: { metrics: OpsMetricConfig[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-lg">Rating Scale</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-56">Metric</TableHead>
              {RATING_LABELS.map((label, i) => (
                <TableHead key={label} className="text-right">
                  <span
                    className={cn(
                      "mr-1.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full border px-1 text-[9px] font-semibold",
                      RATING_STYLES[i + 1],
                    )}
                  >
                    {i + 1}
                  </span>
                  {label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {metrics.map((config) => (
              <TableRow key={config.key}>
                <TableCell className="font-serif text-sm">{config.label}</TableCell>
                {config.bands.map((band, i) => (
                  <TableCell
                    // biome-ignore lint/suspicious/noArrayIndexKey: bands are fixed-position
                    key={i}
                    className="text-right text-sm tabular-nums text-muted-foreground"
                  >
                    {formatOpsValue(config.format, band)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
